/**
 * Durable player state.
 *
 * QA found no cookies, no localStorage and no IndexedDB after full play
 * sessions, so everything the UI implied was remembered — the session spend
 * limit, play history, the once-per-player free card, the welcome modal —
 * reset on every reload. The session limit is the serious one: a
 * responsible-play control that evaporates on refresh is worse than none,
 * because it tells the player they are protected when they are not.
 *
 * What is deliberately NOT stored
 * ------------------------------
 * The balance. For a wallet player it is the contract's number and reading it
 * from anywhere else would invent money; for a demo player it is not real in
 * the first place. Persisting it would recreate, in durable form, exactly the
 * phantom-credit bug this release fixes. `bal` is therefore always derived —
 * from the chain, or from zero.
 *
 * Storage can throw rather than merely return null: Safari in private mode
 * historically threw on setItem, and some embedded webviews block it outright.
 * Every access here is guarded, and a blocked store degrades to the old
 * in-memory behaviour rather than taking the game down.
 */

const KEY = "scratchy:v1";

export interface PlayerState {
  /** Responsible-play cap, in display units. null = no limit set. */
  spendLimit: number | null;
  /** Spent against that cap. Persisted with it, or the cap means nothing. */
  spentSession: number;
  /** Paid cards played. */
  played: number;
  /** Winnings across paid cards, in display units. */
  totalWon: number;
  /** Best single payout, in display units. */
  best: number;
  /** Consecutive wins. */
  streak: number;
  /** The LAST 12 strip. */
  history: { prize: number; big: boolean; even: boolean; part: boolean }[];
  /** Cards scratched, for the ticker. */
  scratched: number;
  /** The free onboarding card is once per player, not once per page load. */
  freeCardUsed: boolean;
  /** Welcome modal is an introduction, so it should introduce once. */
  welcomeSeen: boolean;
}

export const EMPTY: PlayerState = {
  spendLimit: null,
  spentSession: 0,
  played: 0,
  totalWon: 0,
  best: 0,
  streak: 0,
  history: [],
  scratched: 0,
  freeCardUsed: false,
  welcomeSeen: false
};

/** True when the browser will actually keep what we write. */
export function storageAvailable(): boolean {
  try {
    const probe = KEY + ":probe";
    localStorage.setItem(probe, "1");
    localStorage.removeItem(probe);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Read stored state, falling back to EMPTY for anything missing or corrupt.
 *
 * Field-by-field rather than a blanket spread: a stored blob from an older
 * build, or one a user has edited, must not be able to put a string where the
 * game expects a number and break arithmetic several frames later.
 */
export function load(): PlayerState {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...EMPTY };
    const j = JSON.parse(raw);
    const num = (v: any, d: number) => (typeof v === "number" && isFinite(v) && v >= 0 ? v : d);
    return {
      spendLimit: typeof j.spendLimit === "number" && isFinite(j.spendLimit) && j.spendLimit > 0 ? j.spendLimit : null,
      spentSession: num(j.spentSession, 0),
      played: num(j.played, 0),
      totalWon: num(j.totalWon, 0),
      best: num(j.best, 0),
      streak: num(j.streak, 0),
      history: Array.isArray(j.history)
        ? j.history
            .filter((h: any) => h && typeof h.prize === "number")
            .slice(-12)
            .map((h: any) => ({ prize: h.prize, big: !!h.big, even: !!h.even, part: !!h.part }))
        : [],
      scratched: num(j.scratched, 0),
      freeCardUsed: !!j.freeCardUsed,
      welcomeSeen: !!j.welcomeSeen
    };
  } catch (e) {
    return { ...EMPTY };
  }
}

export function save(state: PlayerState): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch (e) {
    /* Quota or a blocked store. Nothing to do but keep playing. */
  }
}

/** Forget everything. Offered so a player can reset their own stats. */
export function clear(): void {
  try {
    localStorage.removeItem(KEY);
  } catch (e) {}
}
